import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { signAsset } from '@/api/assets';
import { uploadSignedAsset } from '@/api/misc';
import { patchDocument } from '@/api/Sherpa/Draw';
import { notifyUser } from '@/store/Toast';
import { sanitizeResponseError } from '@/utils';

type UploadTemplateFunction = (data: { templateFile: File[]; projectId: number; drawId: number; documentId: number }) => Promise<void>;

interface IUseDocumentsTemplateFileUpload {
  uploadTemplate: UploadTemplateFunction;
  isLoading: boolean;
  uploadSuccess: boolean;
  isError: boolean;
}

export const useDocumentsTemplateFileUpload = (): IUseDocumentsTemplateFileUpload => {
  const projectIdRef = useRef<number>(null);
  const documentIdRef = useRef<number>(null);
  const templateIdRef = useRef<number>(null);
  const templateFileRef = useRef<File[]>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const dispatch = useDispatch();

  const uploadAsset = useMutation(
    ({ sign, files }: { sign: any[]; files: File[] }) =>
      Promise.allSettled(
        sign.map(({ value }, index) =>
          uploadSignedAsset({
            file: files[index],
            url: value.url,
          })
        )
      ),
    {
      onSuccess: (_, { sign, files }) => {
        const assetsFiles = files.map((file: any, index: number) => {
          return {
            name: file.name,
            key: sign[index].value.fKey,
            contentType: file.type,
            size: file.size,
          };
        });
        updateDocumentsTemplate.mutate({
          data: {
            documents: assetsFiles,
          },
          drawId: templateIdRef.current!,
          projectId: projectIdRef.current!,
          documentId: documentIdRef.current!,
        });
      },
      onError: (_error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Failed to upload Document File. Try again.',
            toastType: 'failed',
          })
        );
      },
    }
  );

  const signAssetFiles = useMutation(
    (payloads: {
      signAssetPayload: {
        key: string;
        type: string;
        resource?: string;
      }[];
    }) => Promise.allSettled(payloads.signAssetPayload.map((payload) => signAsset(payload))),
    {
      onSuccess: (data) => {
        uploadAsset.mutate({
          sign: data,
          files: templateFileRef.current!,
        });
      },
    }
  );

  const updateDocumentsTemplate = useMutation(
    (payload: { data: any; drawId: number; projectId: number; documentId: number }) => patchDocument({ ...payload }),
    {
      onSuccess: () => {
        setUploadSuccess(true);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const uploadTemplate = useCallback<UploadTemplateFunction>(
    async ({ templateFile, projectId, drawId, documentId }) => {
      templateFileRef.current = templateFile;
      projectIdRef.current = projectId;
      templateIdRef.current = drawId;
      documentIdRef.current = documentId;

      const signPayload = templateFile.map<{
        key: string;
        type: string;
        resource: string;
      }>((file) => ({
        key: file.name,
        type: file.type,
        resource: 'projectDrawDocument',
      }));

      const payload = {
        signAssetPayload: signPayload,
      };

      signAssetFiles.mutate(payload);
    },
    [signAssetFiles]
  );

  return {
    uploadTemplate,
    isLoading: signAssetFiles.isLoading || updateDocumentsTemplate.isLoading || uploadAsset.isLoading,
    uploadSuccess: uploadSuccess,
    isError: signAssetFiles.isError || updateDocumentsTemplate.isError || uploadAsset.isError,
  };
};
